import { useLazyQuery, useMutation } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { addAddOn, removeAddOn } from '../features/addOns/addOnsSlice';
import { setPotentialFranchiseId } from '../features/booking/bookingSlice';
import { fieldsRefused } from '../util/bookingKey';
import {
  FIND_POTENTIAL_FRANCHISE,
  GET_MILEAGE_CHARGE,
  GET_MILEAGE_CHARGE_FOR_SKIN,
} from '../util/gql';
import { ServiceNameKeys } from '../util/serviceKeys';

/**
 * Before the calendar: which franchise serves the address (availability,
 * prepay) and its travel charge for a far address, put in the basket as an
 * add-on. Moved out of routes/Services.tsx so the consumer skin's plan step
 * does exactly the same; `skin: 'consumer'` asks the server to absorb a
 * travel charge under $5 (owner, Aperio).
 */
export default function usePrepareSchedule() {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector((state) => state.customer.id);
  const franchiseIdSelect = useAppSelector((state) => state.franchise.id);
  const { franchiseId } = useAppSelector((state) => state.auth.currentUser);
  const { serviceAddress } = useAppSelector((s) => s.serviceLocation);
  const selectedAddOns = useAppSelector((state) => state.addOns.addOns);

  const [findPotentialFranchise, {
    loading,
  }] = useMutation(FIND_POTENTIAL_FRANCHISE);

  const [getMileageCharge] = useLazyQuery(GET_MILEAGE_CHARGE, {
    fetchPolicy: 'no-cache',
  });
  const [getMileageChargeForSkin] = useLazyQuery(GET_MILEAGE_CHARGE_FOR_SKIN, {
    fetchPolicy: 'no-cache',
  });

  /** Throws when the franchise lookup fails (the caller logs it). */
  const prepare = async (skin?: 'consumer'): Promise<number | null> => {
    const response = await findPotentialFranchise({
      variables: {
        addressInput: serviceAddress,
        franchiseIdSelect: franchiseIdSelect || franchiseId,
        customerId,
      },
    });
    const potentialFranchiseId = response?.data?.findPotentialFranchise;
    dispatch(setPotentialFranchiseId(potentialFranchiseId));

    if (potentialFranchiseId != null) {
      const variables = {
        franchiseId: potentialFranchiseId,
        addressInput: serviceAddress,
        customerId,
      };
      let mileageData;
      if (skin) {
        const answer = await getMileageChargeForSkin({
          variables: { ...variables, skin },
        });
        mileageData = answer.data;
        if (!mileageData && fieldsRefused(answer.error, ['skin'])) {
          mileageData = (await getMileageCharge({ variables })).data;
        }
      } else {
        ({ data: mileageData } = await getMileageCharge({ variables }));
      }

      const mileageChargeAddOn = mileageData?.getMileageChargeAddOnPrice;
      // The basket now survives Back (QA #9), so a second Next must not
      // add the mileage charge a second time: replace it.
      selectedAddOns
        .filter((addOn) => addOn.nameKey === ServiceNameKeys.mileageCharge)
        .forEach((addOn) => dispatch(removeAddOn(addOn.id)));
      if (mileageChargeAddOn && mileageChargeAddOn.length !== 0) {
        dispatch(addAddOn(mileageChargeAddOn[0]));
      }
    }
    return potentialFranchiseId ?? null;
  };

  return { prepare, loading };
}
